import { get, put } from '@eventdex/api/src/services/request';
import { urlFor_PublicApi } from '@eventdex/api/src/services/apiUrl';
import { FetchedAssignment, SeatBackend, SeatUpdate, SeatUpdateResult } from './types';
import { assignmentsFromRows } from './seatRows';

/**
 * The production backend for the Seat Assignment module: the existing Salesforce
 * Apex endpoints, used exactly as they are (no Apex or Portal change — ADR-0002).
 *
 *  - Reads assignments from `BLN_ASC_GetTables`.
 *  - Writes seats to `BLN_updateSeatNo`, whose `SeatNo` carries the Table Label.
 *
 * Both calls go through the shared `@eventdex/api` request service, the way every
 * other Salesforce call in this application does. That service owns the base URL
 * and attaches the credentials; calling `fetch` directly here bypassed both and
 * the endpoints answered with an auth error.
 *
 * The adapter stays thin, and substituting it stays easy, because the module
 * depends on the `SeatBackend` interface rather than on this file.
 */
export interface SalesforceSeatBackendOptions {
  eventId: string;
  /** Extra request headers, if a caller needs them. */
  headers?: Record<string, string>;
}

export { assignmentsFromRows };

/**
 * The rows `BLN_ASC_GetTables` returns, flattened. The Seat Assignments live in
 * them, and so does everything a seating view needs to draw an attendee, so the
 * views read the rows once and take both from the same response.
 */
export async function fetchSeatRows(options: SalesforceSeatBackendOptions): Promise<any[]> {
  const { eventId, headers } = options;
  const endPoint = '/services/apexrest/BLN_ASC_GetTables';
  const params = eventId ? { eventid: eventId } : {};

  const response = await get({ url: urlFor_PublicApi(endPoint, params), headers });
  const data = response?.data;
  if (!Array.isArray(data)) return [];
  return data.flat(3);
}

export function createSalesforceSeatBackend(options: SalesforceSeatBackendOptions): SeatBackend {
  const { headers } = options;

  return {
    async fetchAssignments(): Promise<FetchedAssignment[]> {
      return assignmentsFromRows(await fetchSeatRows(options));
    },

    async updateSeats(updates: SeatUpdate[]): Promise<SeatUpdateResult[]> {
      if (updates.length === 0) return [];
      const endPoint = '/services/apexrest/BLN_updateSeatNo';

      const response = await put({
        url: urlFor_PublicApi(endPoint),
        data: updates,
        headers,
      });
      const results = response?.data;
      if (!Array.isArray(results)) {
        throw new Error('BLN_updateSeatNo returned an unexpected response');
      }
      return results as SeatUpdateResult[];
    },
  };
}
